import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const PromoRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const PromoInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.12);
  }
`;

const ApplyButton = styled.button`
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid #043663;
  background: #fff;
  color: #043663;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: ${(p) => p.$color || 'var(--text-secondary, #64748b)'};
`;

/**
 * Promo code input with explicit Apply — used on registration checkout steps.
 */
export default function RegistrationPromoCodeField({
  id = 'promoCode',
  value,
  onChange,
  onApply,
  applying = false,
  valid = false,
  message = '',
  disabled = false,
}) {
  const { t } = useTranslation();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!applying && !disabled && (value || '').trim()) {
        onApply();
      }
    }
  };

  const helpColor = valid
    ? '#10b981'
    : message && !valid
      ? '#ef4444'
      : 'var(--text-secondary, #64748b)';

  return (
    <>
      <PromoRow>
        <PromoInput
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('register.promoCodePlaceholder')}
          disabled={disabled || applying}
          autoComplete="off"
        />
        <ApplyButton
          type="button"
          onClick={onApply}
          disabled={disabled || applying || !(value || '').trim()}
        >
          {applying
            ? t('register.applyingPromo', 'Applying…')
            : t('register.applyPromo', 'Apply')}
        </ApplyButton>
      </PromoRow>
      <HelpText $color={helpColor}>
        {message ||
          t(
            'register.promoCodeApplyHelp',
            'Enter a code and click Apply to see if it is valid.'
          )}
      </HelpText>
    </>
  );
}
